package br.com.vvrefeicoes.portal.feature.monitor

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.domain.model.PublicMonitor
import br.com.vvrefeicoes.portal.domain.model.PublicMonitorAnalytics
import br.com.vvrefeicoes.portal.domain.model.PublicMonitorService
import kotlinx.coroutines.delay
import kotlinx.datetime.LocalTime
import kotlinx.datetime.UtcOffset
import kotlinx.datetime.asTimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.painterResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.logo_vv
import kotlin.time.Clock

private val MonitorBlue = Color(0xFF2864E8)
private val MonitorBlueLight = Color(0xFFF1F5FF)
private val CardGreen = Color(0xFFF0FAF5)
private val CardBlue = Color(0xFFF1F5FF)
private val CardYellow = Color(0xFFFFFAEF)
private val CardRed = Color(0xFFFFF3F4)
private val CardBorder = Color(0xFFDDE5F1)
private val MutedText = Color(0xFF61728F)

// The backend business zone is UTC-04:00. A fixed offset keeps Wasm independent
// from the browser's optional IANA timezone database.
private val BusinessTimeZone = UtcOffset(hours = -4).asTimeZone()

@Composable
fun PublicMonitorScreen(code: String) {
    val repository = LocalAppGraph.current.companyRepository
    var monitor by remember(code) { mutableStateOf<PublicMonitor?>(null) }
    var error by remember(code) { mutableStateOf<String?>(null) }
    var analytics by remember(code) { mutableStateOf<PublicMonitorAnalytics?>(null) }
    var socketVersion by remember(code) { mutableStateOf(0) }
    var now by remember {
        mutableStateOf(
            Clock.System
                .now()
                .toLocalDateTime(BusinessTimeZone)
                .time,
        )
    }

    LaunchedEffect(Unit) {
        while (true) {
            now =
                Clock.System
                    .now()
                    .toLocalDateTime(BusinessTimeZone)
                    .time
            delay(1_000)
        }
    }

    LaunchedEffect(code, socketVersion) {
        runCatching { repository.resolvePublicMonitor(code) }
            .onSuccess { monitor = it }
            .onFailure { error = "Monitor não encontrado" }
    }

    DisposableEffect(monitor?.companyId) {
        val companyId = monitor?.companyId ?: return@DisposableEffect onDispose { }
        val socket = PublicMonitorSocket(companyId) { socketVersion++ }
        onDispose { socket.close() }
    }

    LaunchedEffect(monitor?.companyId, socketVersion) {
        val companyId = monitor?.companyId ?: return@LaunchedEffect
        while (true) {
            runCatching { repository.loadPublicMonitorAnalytics(companyId) }
                .onSuccess { analytics = it }
                .onFailure { error = "Não foi possível carregar os dados do monitor" }
            delay(30_000)
        }
    }

    Column(Modifier.fillMaxSize().background(Color(0xFFFBFCFE))) {
        MonitorTopBar()
        Box(Modifier.fillMaxSize().weight(1f), contentAlignment = Alignment.TopCenter) {
            when {
                error != null -> Text(error!!, color = MaterialTheme.colorScheme.error)
                monitor == null -> CircularProgressIndicator(color = MonitorBlue)
                else -> MonitorDashboard(monitor!!, analytics, now)
            }
        }
    }
}

@Composable
private fun MonitorTopBar() {
    Surface(
        modifier = Modifier.fillMaxWidth().height(68.dp),
        color = Color.White,
        shadowElevation = 2.dp,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Image(
                painter = painterResource(Res.drawable.logo_vv),
                contentDescription = "VV Refeições",
                modifier = Modifier.height(30.dp),
            )
        }
    }
}

@Composable
private fun MonitorDashboard(
    monitor: PublicMonitor,
    analytics: PublicMonitorAnalytics?,
    now: LocalTime,
) {
    val services =
        (monitor.services + analytics?.services.orEmpty())
            .distinctBy { service -> "${service.name}|${service.startTime}|${service.endTime}" }
    val activeService = services.firstOrNull { isActiveService(it.startTime, it.endTime, now) }
    val nextService = services.minByOrNull { service -> secondsUntil(service.startTime, now) }
    val ratings = activeService?.let { analytics?.ratingsByService?.get(it.name) }.orEmpty()
    val totalVotes = activeService?.let { analytics?.totalVotesByService?.get(it.name) } ?: 0
    val positiveVotes = (ratings["Ótimo"] ?: 0) + (ratings["Bom"] ?: 0)
    val satisfaction = if (totalVotes == 0) 0 else positiveVotes * 1000 / totalVotes
    val serviceName = activeService?.name ?: "Intervalo"
    val nextServiceText =
        if (activeService == null && nextService != null) {
            "${nextService.name} começa em ${formatDuration(secondsUntil(nextService.startTime, now))}"
        } else {
            ""
        }

    Column(
        modifier = Modifier.fillMaxWidth(0.9f).padding(top = 52.dp, bottom = 40.dp),
        verticalArrangement = Arrangement.spacedBy(30.dp),
    ) {
        MonitorSummary(
            companyName = monitor.companyName,
            buttonCount = monitor.buttonCount,
            serviceName = serviceName,
            serviceTime = activeService?.let { formatTimeRange(it) } ?: "",
            currentTime = formatClock(now),
            nextServiceText = nextServiceText,
        )
        StatisticsPanel(
            buttonCount = monitor.buttonCount,
            ratings = ratings,
            totalVotes = totalVotes,
            satisfactionTenths = satisfaction,
        )
    }
}

@Composable
private fun MonitorSummary(
    companyName: String,
    buttonCount: Int,
    serviceName: String,
    serviceTime: String,
    currentTime: String,
    nextServiceText: String,
) {
    Surface(
        modifier = Modifier.fillMaxWidth().border(1.dp, Color(0xFFD8E3FF), RoundedCornerShape(22.dp)),
        shape = RoundedCornerShape(22.dp),
        color = MonitorBlueLight,
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 26.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier.size(40.dp).clip(RoundedCornerShape(12.dp)).background(Color(0xFFE0E9FF)),
                contentAlignment = Alignment.Center,
            ) {
                Text("⌁", color = MonitorBlue, style = MaterialTheme.typography.headlineSmall)
            }
            Spacer(Modifier.width(16.dp))
            Column {
                Text("Monitor", style = MaterialTheme.typography.headlineSmall, color = Color(0xFF0B1220))
                Text("$companyName · $buttonCount botões", color = MutedText, style = MaterialTheme.typography.bodyMedium)
            }
            Spacer(Modifier.weight(1f))
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Text(serviceName, style = MaterialTheme.typography.headlineLarge, color = MonitorBlue)
                if (serviceTime.isNotBlank()) Text(serviceTime, color = MutedText, style = MaterialTheme.typography.bodyMedium)
                Text(currentTime, color = MutedText, style = MaterialTheme.typography.titleMedium)
                if (nextServiceText.isNotBlank()) Text(nextServiceText, color = MutedText, style = MaterialTheme.typography.bodySmall)
            }
            Spacer(Modifier.weight(1f))
        }
    }
}

@Composable
private fun StatisticsPanel(
    buttonCount: Int,
    ratings: Map<String, Int>,
    totalVotes: Int,
    satisfactionTenths: Int,
) {
    val faces =
        listOf(
            RatingCardData("😋", "Ótimo", Color(0xFF36BF67), CardGreen),
            RatingCardData("😃", "Bom", Color(0xFF3779ED), CardBlue),
            RatingCardData("😐", "Regular", Color(0xFFE5A800), CardYellow),
            RatingCardData("😢", "Ruim", Color(0xFFE94951), CardRed),
        ).take(if (buttonCount == 3) 3 else 4)
    val satisfactionText = "${satisfactionTenths / 10}.${satisfactionTenths % 10}%"

    Surface(
        modifier = Modifier.fillMaxWidth().border(1.dp, CardBorder, RoundedCornerShape(8.dp)),
        shape = RoundedCornerShape(8.dp),
        color = Color.White,
    ) {
        Column(Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("▥  Estatísticas de Votos", style = MaterialTheme.typography.titleMedium, color = Color(0xFF0B1220))
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val cards =
                    buildList {
                        add(
                            StatCardValues(
                                emoji = "😊",
                                label = "Satisfação",
                                value = satisfactionText,
                                subtitle = "",
                                color = Color(0xFF0B1220),
                                background = CardBlue,
                                progress = satisfactionTenths / 1000f,
                            ),
                        )
                        faces.forEach { card ->
                            val count = ratings[card.label] ?: 0
                            add(
                                StatCardValues(
                                    emoji = card.emoji,
                                    label = card.label,
                                    value = count.toString(),
                                    subtitle = percentage(count, totalVotes),
                                    color = card.color,
                                    background = card.background,
                                    progress = if (totalVotes == 0) 0f else count.toFloat() / totalVotes,
                                ),
                            )
                        }
                        add(
                            StatCardValues(
                                emoji = "📊",
                                label = "Total",
                                value = totalVotes.toString(),
                                subtitle = "Votos",
                                color = Color(0xFF0B1220),
                                background = CardBlue,
                                progress = null,
                            ),
                        )
                    }
                val compact = maxWidth < 900.dp
                Row(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .then(if (compact) Modifier.horizontalScroll(rememberScrollState()) else Modifier),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    cards.forEach { card ->
                        StatCard(
                            modifier = if (compact) Modifier.width(168.dp) else Modifier.weight(1f),
                            values = card,
                        )
                    }
                }
            }
        }
    }
}

private data class RatingCardData(
    val emoji: String,
    val label: String,
    val color: Color,
    val background: Color,
)

private data class StatCardValues(
    val emoji: String,
    val label: String,
    val value: String,
    val subtitle: String,
    val color: Color,
    val background: Color,
    val progress: Float?,
)

@Composable
private fun StatCard(
    modifier: Modifier,
    values: StatCardValues,
) {
    Surface(
        modifier = modifier.height(164.dp).border(1.dp, CardBorder, RoundedCornerShape(8.dp)),
        shape = RoundedCornerShape(8.dp),
        color = values.background,
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(horizontal = 14.dp, vertical = 12.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(values.emoji, style = MaterialTheme.typography.headlineMedium)
            Text(values.label, color = MutedText, style = MaterialTheme.typography.titleMedium)
            Text(values.value, color = values.color, style = MaterialTheme.typography.headlineLarge)
            if (values.subtitle.isNotBlank()) Text(values.subtitle, color = MutedText, style = MaterialTheme.typography.bodyMedium)
            Spacer(Modifier.weight(1f))
            if (values.progress != null) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .height(5.dp)
                        .clip(RoundedCornerShape(3.dp))
                        .background(Color.White),
                ) {
                    Box(Modifier.fillMaxWidth(values.progress.coerceIn(0f, 1f)).fillMaxSize().background(MonitorBlue))
                }
            }
        }
    }
}

private fun percentage(
    value: Int,
    total: Int,
): String = if (total == 0) "0.0%" else "${value * 1000 / total / 10}.${value * 1000 / total % 10}%"

private fun formatTimeRange(service: PublicMonitorService): String = "${service.startTime.take(5)} - ${service.endTime.take(5)}"

private fun isActiveService(
    start: String,
    end: String,
    now: LocalTime,
): Boolean {
    val startTime = parseServiceTime(start) ?: return false
    val endTime = parseServiceTime(end) ?: return false
    return if (startTime <= endTime) now >= startTime && now <= endTime else now >= startTime || now <= endTime
}

private fun parseServiceTime(value: String): LocalTime? =
    runCatching { LocalTime.parse(value.trim().take(12)) }
        .getOrNull()

private fun secondsUntil(
    start: String,
    now: LocalTime,
): Int {
    val startTime = parseServiceTime(start) ?: return Int.MAX_VALUE
    val difference = secondsOfDay(startTime) - secondsOfDay(now)
    return if (difference > 0) difference else difference + 24 * 60 * 60
}

private fun secondsOfDay(time: LocalTime): Int = time.hour * 60 * 60 + time.minute * 60 + time.second

private fun formatDuration(totalSeconds: Int): String {
    val hours = totalSeconds / 3600
    val minutes = totalSeconds % 3600 / 60
    val seconds = totalSeconds % 60
    return "${twoDigits(hours)}:${twoDigits(minutes)}:${twoDigits(seconds)}"
}

private fun formatClock(time: LocalTime): String = "${twoDigits(time.hour)}:${twoDigits(time.minute)}"

private fun twoDigits(value: Int): String = value.toString().padStart(2, '0')
